import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

/** Shared arrow styling so callers can build typed router <Link>s that match the pager. Plain chevrons —
 *  no bordered box — so they read as the same family as the "‹ All roles" back link beside the title,
 *  rather than a pair of buttons. Muted at rest, foreground on hover. */
export const PAGER_ARROW =
  "grid size-6 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";

function Disabled({ dir }: { dir: "left" | "right" }) {
  return (
    <span className={cn(PAGER_ARROW, "pointer-events-none text-muted-foreground/25")} aria-disabled="true">
      {dir === "left" ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
    </span>
  );
}

/** "‹ Noun i / N ›" record pager — the count sits BETWEEN the arrows (the standard record-stepper
 *  layout: prev · position · next), and the arrows are shaft-less chevrons matching the back link, so
 *  the whole nav row reads as one family. Callers pass their own typed prev/next <Link>s (styled with
 *  PAGER_ARROW), or null for an end — the disabled arrow is rendered here. */
export function Pager({
  noun,
  index,
  total,
  prev,
  next,
}: {
  noun: string;
  index: number;
  total: number;
  prev: React.ReactNode;
  next: React.ReactNode;
}) {
  return (
    <nav aria-label={`${noun} navigation`} className="flex items-center gap-1.5 text-xs">
      {prev ?? <Disabled dir="left" />}
      <span className="whitespace-nowrap text-muted-foreground">
        {noun} <span className="tabular-nums text-foreground">{index + 1}</span> / {total}
      </span>
      {next ?? <Disabled dir="right" />}
    </nav>
  );
}
